if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      euKlarnaBootstrapInterval = undefined;
      euKlarnaPriceObserver = undefined;

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.initQuantityHandlers();
        this.setupEuKlarnaCard();
        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true }),
        );
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(".product-form__quantity");
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this),
          );
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        if (this.euKlarnaBootstrapInterval) {
          window.clearInterval(this.euKlarnaBootstrapInterval);
          this.euKlarnaBootstrapInterval = undefined;
        }
        this.euKlarnaPriceObserver?.disconnect();
        this.euKlarnaPriceObserver = undefined;
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach((element) =>
              element.classList.add("scroll-trigger--cancel"),
            ),
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage,
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              console.log("Fetch aborted by user");
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "variant-selects [data-selected-variant]",
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false,
      ) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector("variant-selects");
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks,
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false,
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(
              `#${id}-${this.dataset.section}`,
            );
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle("hidden", shouldHide(source));
            }
          };

          updateSourceFromDestination("modals");
          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) =>
            classList.contains("hidden"),
          );
          updateSourceFromDestination(
            "Inventory",
            ({ innerText }) => innerText === "",
          );
          updateSourceFromDestination("Volume");
          updateSourceFromDestination("Price-Per-Item", ({ classList }) =>
            classList.contains("hidden"),
          );

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`,
          )?.classList.remove("hidden");
          this.querySelector(
            `#Volume-Note-${this.dataset.section}`,
          )?.classList.remove("hidden");

          this.productForm?.toggleSubmitButton(
            html
              .getElementById(`ProductSubmitButton-${this.sectionId}`)
              ?.hasAttribute("disabled") ?? true,
            window.variantStrings.soldOut,
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          this.setupEuKlarnaCard();
        };
      }

      shouldUseEuKlarnaCard() {
        return (
          window.location.hostname.endsWith("speediance.eu") &&
          Boolean(
            this.querySelector(
              `[data-klarna-card][data-section-id="${this.dataset.section}"]`,
            ) || this.querySelector("[data-klarna-card]"),
          )
        );
      }

      getEuKlarnaPriceRoot() {
        return this.querySelector(`#price-${this.dataset.section}`);
      }

      getEuKlarnaPriceAmount() {
        const priceRoot = this.getEuKlarnaPriceRoot();
        if (!priceRoot) return null;

        const selectors = [
          ".price__sale .price-item--sale",
          ".price__sale .price-item--last",
          ".price-item--sale",
          ".price__regular .price-item--regular",
          ".price-item--regular",
        ];

        const priceElement = selectors
          .map((selector) => priceRoot.querySelector(selector))
          .find((node) => node && node.textContent.trim().length);

        const raw = (priceElement?.textContent || priceRoot.textContent || "")
          .replace(/[^0-9,.\-]/g, "");
        if (!raw) return null;

        const lastComma = raw.lastIndexOf(",");
        const lastDot = raw.lastIndexOf(".");
        const normalized =
          lastComma > lastDot
            ? raw.replace(/\./g, "").replace(",", ".")
            : raw.replace(/,/g, "");

        const amount = Number.parseFloat(normalized);
        return Number.isFinite(amount) ? amount : null;
      }

      formatEuKlarnaMoney(value, currencyCode) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || undefined, {
            style: "currency",
            currency: currencyCode,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(value);
        } catch (error) {
          return `${currencyCode} ${value.toFixed(2)}`;
        }
      }

      ensureEuKlarnaStyles() {
        if (document.getElementById("eu-klarna-card-styles")) return;

        const style = document.createElement("style");
        style.id = "eu-klarna-card-styles";
        style.textContent = `
/* EU product-page finance line - Klarna, inline (2026-08-14).
   Replaced the bordered pink card (.finance-notification--klarna /
   __shell / __copy / __eyebrow / __brand logo / __headline / __caption) with a
   single sentence under the price. Do not reintroduce those classes.
   Mirrored by assets/component-klarna-notification.css - keep both in step. */
.bnpl-inline {
  font-family: 'Helvetica Now Var', 'Helvetica Now', Helvetica, Arial, sans-serif;
  margin: 0.7rem 0 1.6rem;
  max-width: 56rem;
}
.bnpl-inline__line {
  margin: 0;
  font-size: 1.4rem;
  line-height: 1.45;
  color: #111;
}
.bnpl-inline__main { font-weight: 500; }
.bnpl-inline__amount {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.bnpl-inline__brand {
  display: inline-block;
  padding: 0 0.7rem 0.1rem;
  border-radius: 1rem;
  background: #FFB3C7;
  color: #0A0A0A;
  font-weight: 800;
  letter-spacing: -0.01em;
  line-height: 1.35;
  white-space: nowrap;
}
/* The schedule plus the Learn more link always take their own line so the
   link never orphans when the amount runs long in a wide currency format. */
.bnpl-inline__note {
  display: block;
  margin-top: 0.15rem;
  color: rgba(17, 17, 17, 0.68);
  font-size: 1.2rem;
}
.bnpl-inline__learn {
  font-size: 1.2rem;
  font-weight: 600;
  color: #c24f7c;
  text-decoration: underline;
  text-underline-offset: 0.2rem;
}
.bnpl-inline__learn:hover { color: #9c3f63; }
@media (max-width: 749px) {
  .bnpl-inline { max-width: 100%; }
  .bnpl-inline__line { font-size: 1.3rem; }
}
        `;
        document.head.appendChild(style);
      }

      ensureEuKlarnaMarkup() {
        const existing =
          this.querySelector(
            `[data-klarna-card][data-section-id="${this.dataset.section}"]`,
          ) || this.querySelector("[data-klarna-card]");

        const compactMarkup = `
          <p class="bnpl-inline__line">
            <span class="bnpl-inline__main">or 3 interest-free payments of
              <span class="bnpl-inline__amount" data-klarna-payment>&euro;--.--</span>
              with <span class="bnpl-inline__brand">Klarna</span></span>
            <span class="bnpl-inline__note">First payment at checkout, then every 30 days &middot; <a class="bnpl-inline__learn" href="/pages/klarna-buy-now-pay-later">Learn more</a></span>
          </p>
        `;

        if (existing) {
          existing.className = "bnpl-inline";
          existing.dataset.klarnaCard = "";
          existing.dataset.sectionId = this.dataset.section;
          existing.dataset.currencyCode =
            window?.Shopify?.currency?.active || "EUR";

          if (
            !existing.querySelector(".bnpl-inline__line") ||
            !existing.querySelector("[data-klarna-payment]")
          ) {
            existing.innerHTML = compactMarkup;
          }

          return existing;
        }

        const anchor =
          this.querySelector(`#vat-${this.dataset.section}`) ||
          this.querySelector("vat-calculator") ||
          this.getEuKlarnaPriceRoot();
        if (!anchor) return null;

        const card = document.createElement("div");
        card.className = "bnpl-inline";
        card.dataset.klarnaCard = "";
        card.dataset.sectionId = this.dataset.section;
        card.dataset.currencyCode =
          window?.Shopify?.currency?.active || "EUR";
        card.innerHTML = compactMarkup;

        anchor.insertAdjacentElement("afterend", card);
        return card;
      }

      bindEuKlarnaPriceObserver() {
        const priceRoot = this.getEuKlarnaPriceRoot();
        if (!priceRoot) return;

        if (priceRoot.dataset.euKlarnaObserved !== "true") {
          this.euKlarnaPriceObserver?.disconnect();
          this.euKlarnaPriceObserver = new MutationObserver(() => {
            window.requestAnimationFrame(() => this.syncEuKlarnaCard());
          });
          this.euKlarnaPriceObserver.observe(priceRoot, {
            childList: true,
            subtree: true,
            characterData: true,
          });
          priceRoot.dataset.euKlarnaObserved = "true";
        }

        const vatToggle = this.querySelector(
          `#vat-checkbox-${this.dataset.section}`,
        );
        if (vatToggle && vatToggle.dataset.euKlarnaBound !== "true") {
          vatToggle.dataset.euKlarnaBound = "true";
          vatToggle.addEventListener("change", () => {
            window.setTimeout(() => this.syncEuKlarnaCard(), 50);
            window.setTimeout(() => this.syncEuKlarnaCard(), 400);
          });
        }
      }

      syncEuKlarnaCard() {
        if (!this.shouldUseEuKlarnaCard()) return false;

        this.ensureEuKlarnaStyles();
        const card = this.ensureEuKlarnaMarkup();
        if (!card) return false;

        const amount = this.getEuKlarnaPriceAmount();
        if (!amount) return false;

        const currencyCode =
          window?.Shopify?.currency?.active ||
          card.dataset.currencyCode ||
          "EUR";
        card.dataset.currencyCode = currencyCode;

        const paymentNode = card.querySelector("[data-klarna-payment]");
        if (paymentNode) {
          paymentNode.textContent = this.formatEuKlarnaMoney(
            amount / 3,
            currencyCode,
          );
        }

        return true;
      }

      setupEuKlarnaCard() {
        if (!this.shouldUseEuKlarnaCard()) return;

        this.syncEuKlarnaCard();
        this.bindEuKlarnaPriceObserver();

        if (this.euKlarnaBootstrapInterval) {
          window.clearInterval(this.euKlarnaBootstrapInterval);
        }

        this.euKlarnaBootstrapInterval = window.setInterval(() => {
          if (!this.isConnected) {
            window.clearInterval(this.euKlarnaBootstrapInterval);
            this.euKlarnaBootstrapInterval = undefined;
            return;
          }

          this.syncEuKlarnaCard();

          const card = this.querySelector(
            `[data-klarna-card][data-section-id="${this.dataset.section}"]`,
          );
          if (card && card.querySelector("[data-klarna-payment]")) {
            window.clearInterval(this.euKlarnaBootstrapInterval);
            this.euKlarnaBootstrapInterval = undefined;
          }
        }, 1000);

        window.setTimeout(() => {
          if (this.euKlarnaBootstrapInterval) {
            window.clearInterval(this.euKlarnaBootstrapInterval);
            this.euKlarnaBootstrapInterval = undefined;
          }
        }, 15000);
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ""}`,
        );

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}`,
        );
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable,
        );

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Price-Per-Item",
          "Volume-Note",
          "Volume",
          "Quantity-Rules",
        ]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(html, variantFeaturedMediaId) {
        // Filtered main slides, thumbnails and modal must change as one set.
        // Scope to this product section: other product/quick-add galleries stay intact.
        const gallery = this.querySelector('media-gallery[data-bm-variant-gallery]');
        const sourceSection = html.getElementById(`MainProduct-${this.sectionId}`) || html;
        const nextGallery = sourceSection.querySelector('media-gallery[data-bm-variant-gallery]');
        if (gallery && nextGallery) {
          const nextModal = html.getElementById(`ProductModal-${this.sectionId}`);
          const modalContent = this.productModal?.querySelector('.product-media-modal__content');
          const nextModalContent = nextModal?.querySelector('.product-media-modal__content');
          if (modalContent && nextModalContent) modalContent.innerHTML = nextModalContent.innerHTML;
          gallery.replaceWith(nextGallery);
          const firstMediaId = nextGallery.querySelector('[data-media-id]')?.dataset.mediaId;
          if (firstMediaId) nextGallery.setActiveMedia?.(firstMediaId, false);
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          return;
        }

        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector("media-gallery ul");
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute("data-zoom-on-hover")) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(
            mediaGallerySource.querySelectorAll("li[data-media-id]"),
          );
          const sourceSet = new Set(
            mediaGallerySourceItems.map((item) => item.dataset.mediaId),
          );
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [
              item.dataset.mediaId,
              { item, index },
            ]),
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] =
            refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll("li[data-media-id]"),
          );
          const destinationSet = new Set(
            mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId),
          );
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (
              !sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)
            ) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (
              !destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)
            ) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh)
            [mediaGallerySourceItems, sourceSet, sourceMap] =
              refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach(
            (destinationItem, destinationIndex) => {
              const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

              if (sourceData && sourceData.index !== destinationIndex) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  mediaGallerySource.querySelector(
                    `li:nth-of-type(${destinationIndex + 1})`,
                  ),
                );

                // refresh source now that it has been modified
                [mediaGallerySourceItems, sourceSet, sourceMap] =
                  refreshSourceData();
              }
            },
          );
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(
          `.product-media-modal__content`,
        );
        const newModalContent = html.querySelector(
          `product-modal .product-media-modal__content`,
        );
        if (modalContent && newModalContent)
          modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner",
        ).classList.remove("hidden");
        return fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`,
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner",
            ).classList.add("hidden"),
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`,
        );
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label",
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step",
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === ".quantity__label") {
              const updatedAriaLabelledBy =
                updated.getAttribute("aria-labelledby");
              if (updatedAriaLabelledBy) {
                current.setAttribute("aria-labelledby", updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent =
                    updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products",
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`,
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list",
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`,
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    },
  );
}
